import { googleConnectScopes, SCHEDULING_ASSISTANT_GOOGLE_CLIENT_ID } from '@wix/ai-scheduling-assistant-constants';

const GOOGLE_IDENTITY_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

/** The subset of Google Identity Services this module drives. Declared here rather than pulled from a
 *  typings package: one factory and one method is the whole contract we depend on. */
interface GoogleCodeClient {
  requestCode(): void;
}

interface GoogleCodeResponse {
  code?: string;
  error?: string;
}

/** `popup_closed` when the user dismisses the consent window, `popup_failed_to_open` when the browser
 *  blocks it. Google's own spelling of both — kept verbatim so the caller can map them to copy. */
interface GoogleCodeError {
  type?: string;
}

interface GoogleIdentityServices {
  accounts: {
    oauth2: {
      initCodeClient(config: {
        client_id: string;
        scope: string;
        ux_mode: 'popup' | 'redirect';
        prompt?: string;
        include_granted_scopes?: boolean;
        callback: (response: GoogleCodeResponse) => void;
        error_callback?: (error: GoogleCodeError) => void;
      }): GoogleCodeClient;
    };
  };
}

declare global {
  // eslint-disable-next-line no-var
  var google: GoogleIdentityServices | undefined;
}

let scriptLoad: Promise<void> | undefined;

/** Loads Google's script once per page and reuses the same promise, so two connect clicks do not race
 *  two copies of it into the document. */
function loadGoogleIdentityServices(): Promise<void> {
  if (!scriptLoad) {
    scriptLoad = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GOOGLE_IDENTITY_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Let the next click try again: a failed load is usually a blocked request or a dropped
        // connection, not a permanent state of the page.
        scriptLoad = undefined;
        reject(new Error('google_script_blocked'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptLoad;
}

/**
 * Runs Google's consent popup and returns the single-use authorization code it produces — a credential
 * that redeems for a refresh token, so it goes straight to the server and is never stored or logged.
 *
 * Why the popup code flow rather than a redirect: the scheduling-assistant client authorizes
 * `symphony.wix.com` as a JavaScript origin, and an origin is all this flow needs — a redirect flow
 * would need a registered redirect URI per environment, on a client we would have to keep asking
 * someone else to edit.
 *
 * `prompt=consent` forces the account chooser and a fresh offline consent even when the browser already
 * holds a Google session, which is what makes it possible to connect a SECOND account. Without it Google
 * silently re-authorizes the current one.
 *
 * `include_granted_scopes` defaults to TRUE in Google Identity Services, and it has to be turned off for
 * the scope list above to be the whole request. Left on, Google adds every scope the account has already
 * granted this client — so removing a scope from the list does not stop it being asked for, on any account
 * that once granted it. That is not academic: the two contacts scopes were dropped here and kept arriving
 * anyway, out of consent histories, and Google refuses the entire consent over one unapproved scope. A
 * user in that state could not reconnect at all until they revoked the app in their Google account.
 *
 * `includeGmail` comes from the server's own default-off gate, never from anything the browser decides.
 * It defaults to false on an explicit `=== true` rather than on truthiness, because the values that reach
 * it when a read fails — undefined, an empty string, the string "false" — must all mean "do not ask": the
 * Gmail scopes are unapproved, and an account that consents to one spends a slot against a per-project cap
 * that is lifetime and cannot be reset.
 */
export async function requestGoogleCalendarAuthorization(options: { includeGmail?: boolean } = {}): Promise<string> {
  await loadGoogleIdentityServices();
  const identityServices = globalThis.google;
  if (!identityServices) throw new Error('google_script_blocked');

  return new Promise<string>((resolve, reject) => {
    identityServices.accounts.oauth2
      .initCodeClient({
        client_id: SCHEDULING_ASSISTANT_GOOGLE_CLIENT_ID,
        scope: googleConnectScopes({ includeGmail: options.includeGmail === true }).join(' '),
        ux_mode: 'popup',
        prompt: 'consent',
        include_granted_scopes: false,
        callback: (response) => {
          if (response.code) {
            resolve(response.code);
            return;
          }
          reject(new Error(response.error || 'no_authorization_code'));
        },
        error_callback: (error) => reject(new Error(error.type || 'google_consent_failed')),
      })
      .requestCode();
  });
}
